import { useState, useRef, useEffect } from "react";
import { Input, DatePicker, IconButton, SelectPicker } from "rsuite";
import { FaSave, FaTimes, FaEdit } from "react-icons/fa";
import logger from "../../utils/logger";
import "./index.css";

// Field type definitions for different input types
const FIELD_TYPES = {
  TEXT: "text",
  NUMBER: "number",
  DATE: "date",
  TEXTAREA: "textarea",
  SELECT: "select",
};

// Utility functions for date handling (copied from expandedRow.jsx)
const parseShortDateString = (dateString) => {
  if (!dateString) return null;
  try {
    // Handle dd/MM/yy format
    const [day, month, year] = dateString.split("/");
    const fullYear = year.length === 2 ? (parseInt(year) < 50 ? `20${year}` : `19${year}`) : year;
    return new Date(`${fullYear}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`);
  } catch (error) {
    logger.warn("Failed to parse date string:", dateString, error);
    return null;
  }
};

const formatDateToShortString = (date) => {
  if (!date) return "";
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

// Component to render individual editable fields
function EditableField({ fieldKey, fieldConfig, value, isEditing, onChange }) {
  const inputRef = useRef(null);

  // Set initial value when editing starts (only for uncontrolled inputs)
  useEffect(() => {
    if (isEditing && inputRef.current && !fieldConfig.regex && !fieldConfig.format) {
      inputRef.current.value = value || "";
    }
  }, [isEditing, value, fieldConfig.regex, fieldConfig.format]);

  if (!isEditing) {
    return <span>{value || ""}</span>;
  }

  // Use onBlur instead of onChange to reduce re-renders
  const handleInputBlur = () => {
    if (inputRef.current) {
      onChange(fieldKey, inputRef.current.value);
    }
  };

  // For real-time validation/formatting, we'll still need onChange
  const handleInputChange = (val) => {
    let newValue = val;
    
    // If format function exists, use it (it usually handles its own validation)
    if (fieldConfig.format) {
      newValue = fieldConfig.format(val);
    }
    // Otherwise, apply regex filtering if present
    else if (fieldConfig.regex) {
      const re = new RegExp(fieldConfig.regex, 'g');
      const matches = val.match(re);
      newValue = matches ? matches.join("") : "";
    }
    
    onChange(fieldKey, newValue);
  };

  // If a regex or format function is provided in fieldConfig, use controlled input
  if (fieldConfig.regex || fieldConfig.format) {
    return (
      <Input
        ref={inputRef}
        type={fieldConfig.type === FIELD_TYPES.NUMBER ? "number" : "text"}
        value={value || ""}
        onChange={handleInputChange}
        maxLength={fieldConfig.maxLength}
        size="sm"
        style={{ width: "100%" }}
        placeholder={fieldConfig.placeholder}
      />
    );
  }

  switch (fieldConfig.type) {
    case FIELD_TYPES.DATE:
      return (
        <DatePicker
          placeholder="DD/MM/YYYY"
          format="dd/MM/yy"
          oneTap
          cleanable={false}
          value={parseShortDateString(value)}
          onChange={(date) => onChange(fieldKey, formatDateToShortString(date))}
          size="sm"
          style={{ width: "100%" }}
        />
      );

    case FIELD_TYPES.NUMBER:
      return (
        <Input
          ref={inputRef}
          type="number"
          step={fieldConfig.step || "1"}
          defaultValue={value || ""}
          onBlur={handleInputBlur}
          size="sm"
          style={{ width: "100%" }}
        />
      );

    case FIELD_TYPES.TEXTAREA:
      return (
        <Input
          ref={inputRef}
          as="textarea"
          rows={fieldConfig.rows || 3}
          defaultValue={value || ""}
          onBlur={handleInputBlur}
          size="sm"
          style={{ width: "100%" }}
        />
      );

    case FIELD_TYPES.SELECT:
      return (
        <SelectPicker
          value={value}
          defaultValue={value}
          onChange={val => onChange(fieldKey, val)}
          style={{ width: "100%", minWidth: 80 }}
          disabled={fieldConfig.disabled}
          data={fieldConfig.options || []}
          cleanable={false}
          placeholder={fieldConfig.placeholder || "Select..."}
          searchable={false}
        />
      );

    default: // TEXT
      return (
        <Input
          ref={inputRef}
          type="text"
          defaultValue={value || ""}
          onBlur={handleInputBlur}
          size="sm"
          style={{ width: "100%" }}
        />
      );
  }
}

// Hook for managing editable row state
export function useEditableRow({ initialData = {}, onSave, onError }) {
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [saving, setSaving] = useState(false);

  const startEditing = (rowData) => {
    // Set default values for select fields if not present
    const newRowData = { ...rowData };
    if (window && window.FIELD_CONFIG) {
      Object.entries(window.FIELD_CONFIG).forEach(([key, config]) => {
        if (config.type === FIELD_TYPES.SELECT && (!newRowData[key] || newRowData[key] === "")) {
          newRowData[key] = config.options?.[0]?.value || "";
        }
      });
    }
    setEditingRowId(rowData.id);
    setEditingData(newRowData);
  };

  const cancelEditing = () => {
    setEditingRowId(null);
    setEditingData({});
  };

  const handleFieldChange = (field, value) => {
    setEditingData((prev) => ({ ...prev, [field]: value }));
  };

  const saveRow = async () => {
    if (!editingRowId || !editingData) return;

    setSaving(true);
    try {
      await onSave(editingRowId, editingData);
      setEditingRowId(null);
      setEditingData({});
    } catch (error) {
      logger.error("Error saving row:", error);
      if (onError) {
        onError(error);
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    editingRowId,
    editingData,
    saving,
    startEditing,
    cancelEditing,
    handleFieldChange,
    saveRow,
    isEditing: (rowId) => editingRowId === rowId,
  };
}

// Component for rendering action buttons (Edit/Save/Cancel)
export function EditableRowActions({ rowData, isEditing, onEdit, onSave, onCancel, saving = false }) {
  return (
    <div style={{ display: "flex", gap: "4px" }}>
      {isEditing ? (
        <>
          <IconButton icon={<FaSave />} className="edit-save-button" size="xs" appearance="subtle" onClick={onSave} title="Save" loading={saving} disabled={saving} />
          <IconButton icon={<FaTimes />} className="edit-cancel-button" size="xs" appearance="subtle" onClick={onCancel} title="Cancel" disabled={saving} />
        </>
      ) : (
        <IconButton className="edit-button" icon={<FaEdit />} size="xs" appearance="subtle" onClick={() => onEdit(rowData)} title="Edit" />
      )}
    </div>
  );
}

// Main component for creating editable table cells
export function EditableTableCell({ rowData, fieldKey, fieldConfig = { type: FIELD_TYPES.TEXT }, editingRowId, editingData, onFieldChange }) {
  const isEditing = editingRowId === rowData.id;

  return (
    <EditableField
      fieldKey={fieldKey}
      fieldConfig={fieldConfig}
      value={isEditing ? editingData[fieldKey] : rowData[fieldKey]}
      isEditing={isEditing}
      onChange={onFieldChange}
    />
  );
}

// Export field types for easy use
export { FIELD_TYPES };

export default {
  useEditableRow,
  EditableRowActions,
  EditableTableCell,
  FIELD_TYPES,
};
